import { StageType } from '../../../types/enums/StageType';
import { CanvasElementType } from '../../sweep-canvas/canvas-types';
import {
  CanvasGridPosition,
  convertGridPositionToXYPosition,
} from '../../sweep-canvas/helpers/gridPositioningUtils';
import { createObjectOperations } from '../../sweep-canvas/node-changes-event-handlers/objectOperations';
import {
  ShortNodeInfo,
  getNormalizedGroupPositions,
} from '../../sweep-canvas/normalizeGroupPositions';

type ShortNodeInfoWithGridPosition = ShortNodeInfo & {
  gridPosition: CanvasGridPosition;
};

export const normalizeFunnelMapPositions = (funnelMap: FunnelMap) => {
  const groups: ShortNodeInfoWithGridPosition[] = Object.entries({
    ...funnelMap.funnels,
    ...funnelMap.thirdPartyFunnels,
  }).map(([funnelId, funnel]) => ({
    id: funnelId,
    type: CanvasElementType.GROUP,
    position: convertGridPositionToXYPosition(funnel.position, {
      withPaddingByNodeType: CanvasElementType.GROUP,
    }),
    gridPosition: funnel.position,
  }));

  const funnelSteps: ShortNodeInfoWithGridPosition[] = Object.values(funnelMap.funnelsData)
    .map((funnel) =>
      funnel.funnelDetails.stages
        .filter((stage) => stage.stageType !== StageType.LOST)
        .map((stage) => ({
          id: stage._stageId,
          type: CanvasElementType.REGULAR,
          position: convertGridPositionToXYPosition(
            {
              row: stage._branchIndex,
              column: stage._stageColumnIndex as number,
            },
            { withPaddingByNodeType: CanvasElementType.REGULAR },
          ),
          gridPosition: {
            row: stage._branchIndex,
            column: stage._stageColumnIndex as number,
          },
          parentId: funnel.id,
        })),
    )
    .flat();

  const thirdPartySteps: ShortNodeInfoWithGridPosition[] = Object.entries(
    funnelMap.thirdPartyFunnelsData,
  )
    .map(([thirdPartyFunnelId, thirdPartyFunnel]) =>
      thirdPartyFunnel.steps.map((step) => ({
        id: step.id,
        type: CanvasElementType.REGULAR,
        position: convertGridPositionToXYPosition(step.position, {
          withPaddingByNodeType: CanvasElementType.REGULAR,
        }),
        gridPosition: step.position,
        parentId: thirdPartyFunnelId,
      })),
    )
    .flat();

  const steps = [...funnelSteps, ...thirdPartySteps];
  const allSteps = [...groups, ...steps];
  const nodeOperations = createObjectOperations<ShortNodeInfoWithGridPosition>(allSteps);
  const { setObject: setNode, getObject: getNode } = nodeOperations;

  groups.forEach((group) => {
    const { normalizedGroupPosition, normalizedStepPositions } = getNormalizedGroupPositions({
      groupId: group.id,
      nodeOperations,
    });
    setNode(normalizedGroupPosition.id, (node) => ({
      ...node,
      position: normalizedGroupPosition.xyPosition,
      gridPosition: normalizedGroupPosition.position,
    }));
    normalizedStepPositions.forEach((step) => {
      setNode(step.id, (node) => ({
        ...node,
        position: step.xyPosition,
        gridPosition: step.position,
      }));
    });
  });

  const funnels = Object.entries(funnelMap.funnels).map(([funnelId, funnel]) => {
    const groupNode = getNode(funnelId);
    if (!groupNode) {
      return [funnelId, funnel];
    }
    return [
      funnelId,
      {
        ...funnel,
        position: groupNode.gridPosition,
      },
    ];
  });
  const thirdPartyFunnels = Object.entries(funnelMap.thirdPartyFunnels).map(
    ([thirdPartyFunnelId, thirdPartyFunnel]) => {
      const groupNode = getNode(thirdPartyFunnelId);
      if (!groupNode) {
        return [thirdPartyFunnelId, thirdPartyFunnel];
      }
      return [
        thirdPartyFunnelId,
        {
          ...thirdPartyFunnel,
          position: groupNode.gridPosition,
        },
      ];
    },
  );

  const funnelsData: [string, Funnel][] = Object.entries(funnelMap.funnelsData).map(
    ([funnelId, funnel]) => {
      const stages = funnel.funnelDetails.stages.map((stage) => {
        const stageNode = getNode(stage._stageId);
        if (!stageNode) {
          return stage;
        }
        return {
          ...stage,
          _branchIndex: stageNode.gridPosition.row,
          _stageColumnIndex: stageNode.gridPosition.column,
        };
      });

      return [
        funnelId,
        {
          ...funnel,
          funnelDetails: {
            ...funnel.funnelDetails,
            stages,
          },
        },
      ];
    },
  );

  const thirdPartyFunnelsData = Object.entries(funnelMap.thirdPartyFunnelsData).map(
    ([thirdPartyFunnelId, thirdPartyFunnelData]) => {
      const steps = thirdPartyFunnelData.steps.map((step) => {
        const stepNode = getNode(step.id);
        if (!stepNode) {
          return step;
        }
        return {
          ...step,
          position: stepNode.gridPosition,
        };
      });
      return [thirdPartyFunnelId, { ...thirdPartyFunnelData, steps }];
    },
  );

  const ret: FunnelMap = {
    ...funnelMap,
    funnels: Object.fromEntries(funnels),
    thirdPartyFunnels: Object.fromEntries(thirdPartyFunnels),
    funnelsData: Object.fromEntries(funnelsData),
    thirdPartyFunnelsData: Object.fromEntries(thirdPartyFunnelsData),
  };
  return ret;
};
